import { Modal, Pressable, Platform, View } from "react-native"
import { useState } from "react"
import { Show, useObservable } from "@legendapp/state/react"
import { useDebounce } from "use-debounce"
import { SafeAreaView } from "react-native-safe-area-context"

import { arabicToEnglishNumbers } from "@white-labels/utils"

import { ar } from "@src/i18n"
import { cn, sharedStyles, trpc } from "@src/utils"

import { List } from "./list"
import { Header } from "./Header"
import { Text, TextInput, MCIcon, ActivityIndicator } from "./core"

type Props = {
  value?: number
  onChange: (id?: number) => void
  className?: string
  disabled?: boolean
}

export function CustomerPicker({ value, className, onChange, disabled }: Props) {
  const customerModal = useObservable(false)
  const customer = trpc.customers.get.useQuery({ id: value as number }, { enabled: !!value })

  return (
    <>
      <Pressable
        className={cn("h-11 bg-gray-50 border border-gray-300 rounded px-4 justify-center active:opacity-50", className)}
        disabled={disabled}
        onPress={() => customerModal.set(true)}
      >
        <Text className={`text-left ${customer.data ? "text-gray-900" : "text-gray-500"}`}>
          {/*  */}
          {customer.data ? customer.data.name : ar.selectCustomer}
        </Text>
      </Pressable>

      <Show if={customerModal}>
        <CustomerPickModal hide={() => customerModal.set(false)} onSelectionChange={onChange} />
      </Show>
    </>
  )
}

function CustomerPickModal({ onSelectionChange, hide }: { onSelectionChange: (id: number) => void; hide: () => void }) {
  const [name, setName] = useState<string>()
  const [phone, setPhone] = useState<string>()
  const [debouncedName] = useDebounce(name, 500)
  const [debouncedPhone] = useDebounce(phone, 500)

  const isEnabled = (debouncedName?.length ?? 0) >= 3 || (debouncedPhone?.length ?? 0) >= 3

  const customers = trpc.customers.list.useInfiniteQuery(
    { filter: { name: debouncedName || undefined, phone: debouncedPhone || undefined } },
    { getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined, enabled: isEnabled },
  )

  const data = customers.data?.pages.flatMap((page) => page.items) ?? []

  return (
    <Modal animationType="slide" presentationStyle="pageSheet" onRequestClose={hide}>
      <Header
        title={ar.customerLookup}
        withoutInsets={Platform.OS === "ios"}
        canGoBack={false}
        right={
          <Pressable onPress={hide} className="h-full px-2 justify-center">
            <MCIcon name="close" size={25} />
          </Pressable>
        }
      />
      <View className="px-2 mt-2 gap-2 max-w-content mx-auto w-full">
        <TextInput placeholder={ar.name} value={name} onChangeText={setName} />
        <TextInput placeholder={ar.phone} value={phone} onChangeText={(text) => setPhone(arabicToEnglishNumbers(text))} keyboardType="phone-pad" />
      </View>

      <List
        data={data}
        contentContainerStyle={sharedStyles.contentContainer}
        onEndReached={() => {
          if (customers.hasNextPage && !customers.isFetchingNextPage) customers.fetchNextPage()
        }}
        onEndReachedThreshold={0.5}
        ListFooterComponent={
          <>
            {customers.isFetchingNextPage && <ActivityIndicator />}
            <SafeAreaView className="py-2" edges={["bottom"]} />
          </>
        }
        keyExtractor={(item) => item.id.toString()}
        renderItem={({ item: customer }) => {
          const primaryPhone = customer.phoneNumbers.find((p) => p.primary)?.phone || customer.phoneNumbers[0]?.phone
          return (
            <Pressable
              className="py-3 px-4 border-b border-gray-300"
              onPress={() => {
                onSelectionChange(customer.id)
                hide()
              }}
            >
              <Text className="font-bold text-gray-900">{customer.name}</Text>
              {primaryPhone && <Text className="text-gray-500">{primaryPhone}</Text>}
            </Pressable>
          )
        }}
      />
    </Modal>
  )
}
